import { z } from 'zod';
import type {
  ImportInboundReceiptOfficialSettlementCandidateRow,
  ImportInboundReceiptOfficialSettlementRequest,
  ImportInboundReceiptOfficialSettlementRejectedRow,
  ImportInboundReceiptOfficialSettlementResponse,
  ImportInboundReceiptOfficialSettlementSelectedRow,
} from './import-inbound-receipt-official-settlement.dto.js';
import { InboundReceiptDetailDtoSchema } from './inbound-receipts-shared.schema.js';
import { IsoDateSchema } from '../shipments/shipments-types.js';
import { ReportableWeightLbSchema } from '../reportable-weight.schema.js';

const resultTypeSchema: z.ZodType<ImportInboundReceiptOfficialSettlementResponse['resultType']> =
  z.enum(['IMPORTED', 'NO_MATCH', 'AMBIGUOUS', 'INVALID_FILE']);
const matchBasisSchema: z.ZodType<
  ImportInboundReceiptOfficialSettlementCandidateRow['matchBasis']
> = z.enum([
  'ACTIVE_OFFICIAL_SHIPPING_REPORT_KEY',
  'SOURCE_DOCUMENT_SHIPPING_REPORT_KEY',
  'RECEIVED_DATE_AND_WEIGHT_TICKET_NUMBER',
  'RECEIVED_DATE_WEIGHT_TICKET_NUMBER_AND_RECEIVED_WEIGHT',
]);
const actionTakenSchema: z.ZodType<
  ImportInboundReceiptOfficialSettlementSelectedRow['actionTaken']
> = z.enum(['CREATED_ACTIVE', 'REUSED_ACTIVE', 'REPLACED_ACTIVE']);
const rejectionCodeSchema: z.ZodType<
  ImportInboundReceiptOfficialSettlementRejectedRow['rejectionCode']
> = z.enum([
  'MISSING_SHIPPING_REPORT_NUMBER',
  'MISSING_RECEIVED_DATE',
  'MISSING_WEIGHT_TICKET_NUMBER',
  'INVALID_RECEIVED_WEIGHT',
  'INVALID_SHRINKAGE_PERCENT',
  'INVALID_REDUCTION_FACTOR',
  'INVALID_PAYMENT_WEIGHT',
  'INVALID_REFUND_VALUE',
  'INVALID_PROCESSING_FEE',
  'INVALID_ADMINISTRATIVE_FEE',
  'INVALID_TOTAL_PAYMENT_DUE',
  'INVALID_TOTAL_PAYMENT_CALCULATION',
]);

export const ImportInboundReceiptOfficialSettlementRequestSchema: z.ZodType<ImportInboundReceiptOfficialSettlementRequest> =
  z
    .object({
      companyId: z.uuid(),
      receiptId: z.uuid(),
      fileName: z.string().trim().min(1),
      csvText: z.string(),
    })
    .strict();

const ImportInboundReceiptOfficialSettlementCandidateRowSchema = z
  .object({
    snapshotId: z.uuid(),
    rowNumber: z.int().min(1),
    shippingReportNumber: z.string().trim().min(1),
    receivedDate: IsoDateSchema,
    weightTicketNumber: z.string().trim().min(1),
    receivedWeightLb: ReportableWeightLbSchema,
    paymentWeightLb: ReportableWeightLbSchema,
    shrinkagePercent: z.number().min(0).nullable(),
    reductionFactor: z.number().min(0).nullable(),
    refundValueCents: z.int().min(0),
    processingFeeCents: z.int().min(0),
    administrativeFeeCents: z.int().min(0),
    totalPaymentDueCents: z.int(),
    onHold: z.boolean().nullable(),
    matchBasis: matchBasisSchema,
    matchExplanation: z.string().trim().min(1),
  })
  .strict() satisfies z.ZodType<ImportInboundReceiptOfficialSettlementCandidateRow>;

const ImportInboundReceiptOfficialSettlementSelectedRowSchema =
  ImportInboundReceiptOfficialSettlementCandidateRowSchema.extend({
    actionTaken: actionTakenSchema,
  }) satisfies z.ZodType<ImportInboundReceiptOfficialSettlementSelectedRow>;

const ImportInboundReceiptOfficialSettlementRejectedRowSchema = z
  .object({
    rowNumber: z.int().min(1),
    shippingReportNumber: z.string(),
    rejectionCode: rejectionCodeSchema,
    rejectionReason: z.string().trim().min(1),
  })
  .strict() satisfies z.ZodType<ImportInboundReceiptOfficialSettlementRejectedRow>;

export const ImportInboundReceiptOfficialSettlementResponseSchema: z.ZodType<ImportInboundReceiptOfficialSettlementResponse> =
  z
    .object({
      resultType: resultTypeSchema,
      fileName: z.string().trim().min(1),
      parsedRowCount: z.int().min(0),
      validRowCount: z.int().min(0),
      invalidRowCount: z.int().min(0),
      candidateRowCount: z.int().min(0),
      matchBasis: matchBasisSchema.nullable(),
      selectedRow: ImportInboundReceiptOfficialSettlementSelectedRowSchema.nullable(),
      candidateRows: z.array(ImportInboundReceiptOfficialSettlementCandidateRowSchema),
      rejectedRows: z.array(ImportInboundReceiptOfficialSettlementRejectedRowSchema),
      replacedPriorOfficial: z.boolean(),
      receipt: InboundReceiptDetailDtoSchema,
    })
    .strict();
